import "./index.less";

type FeedbackType = "success" | "error" | "warning" | "info";

type FeedbackConfig = {
  id?: string;
  content?: unknown;
  title?: unknown;
  duration?: number;
  className?: string;
  actionText?: unknown;
  onAction?: () => void;
};

type FeedbackInput = string | number | FeedbackConfig | unknown;
type FeedbackPresentation = "compact" | "standard";

const DEFAULT_COMPACT_DURATION = 3;
const DEFAULT_NOTIFICATION_DURATION = 4.5;
const FEEDBACK_ROOT_CLASS = "feedback-ui-root";
const FEEDBACK_RAIL_CLASS = "feedback-ui-notification-rail";

let feedbackRoot: HTMLDivElement | null = null;
let feedbackRail: HTMLDivElement | null = null;
const suspendedNotifications = new Set<string>();
let queuedNotifications: Array<{
  type: FeedbackType;
  input: FeedbackInput;
}> = [];

const canUseDom = () => typeof document !== "undefined";

const toText = (content: unknown): string => {
  if (content === undefined || content === null) {
    return "";
  }

  if (typeof content === "string" || typeof content === "number") {
    return String(content);
  }

  if (typeof content === "object" && "props" in (content as Record<string, unknown>)) {
    return "";
  }

  return String(content);
};

const normalizeConfig = (input: FeedbackInput): FeedbackConfig => {
  if (
    input &&
    typeof input === "object" &&
    !Array.isArray(input) &&
    ("content" in (input as Record<string, unknown>) ||
      "title" in (input as Record<string, unknown>) ||
      "duration" in (input as Record<string, unknown>) ||
      "id" in (input as Record<string, unknown>) ||
      "className" in (input as Record<string, unknown>) ||
      "actionText" in (input as Record<string, unknown>) ||
      "onAction" in (input as Record<string, unknown>))
  ) {
    return input as FeedbackConfig;
  }

  return { content: input };
};

const appendText = (parent: HTMLElement, content: unknown) => {
  parent.textContent = toText(content);
};

const getFeedbackRoot = () => {
  if (!canUseDom()) {
    return null;
  }

  if (!feedbackRoot || !feedbackRoot.isConnected) {
    feedbackRoot = document.createElement("div");
    feedbackRoot.className = FEEDBACK_ROOT_CLASS;
  }

  if (feedbackRoot.parentElement !== document.body) {
    document.body.appendChild(feedbackRoot);
  }

  return feedbackRoot;
};

const getFeedbackRail = () => {
  if (!canUseDom()) {
    return null;
  }

  const root = getFeedbackRoot();

  if (!root) {
    return null;
  }

  if (!feedbackRail || !feedbackRail.isConnected) {
    feedbackRail = document.createElement("div");
    feedbackRail.className = [
      FEEDBACK_RAIL_CLASS,
      "feedback-ui-notification-container",
      "feedback-ui-message-container",
    ].join(" ");
  }

  if (feedbackRail.parentElement !== root) {
    root.appendChild(feedbackRail);
  }

  return feedbackRail;
};

const scheduleRemove = (node: HTMLElement, duration: number | undefined, fallback: number) => {
  const resolvedDuration = duration ?? fallback;

  if (resolvedDuration <= 0) {
    return;
  }

  window.setTimeout(() => {
    node.remove();
  }, resolvedDuration * 1000);
};

const createFeedback = (
  type: FeedbackType,
  input: FeedbackInput,
  presentation: FeedbackPresentation
) => {
  const config = normalizeConfig(input);
  const container = getFeedbackRail();

  if (!container) {
    return undefined;
  }

  const notification = document.createElement("div");
  const isCompact = presentation === "compact";
  notification.className = [
    "feedback-ui-notification-item",
    isCompact ? "feedback-ui-notification-compact" : "feedback-ui-notification-standard",
    "feedback-ui-message",
    "feedback-ui-notification",
    `feedback-ui-message-${type}`,
    `feedback-ui-notification-${type}`,
    config.className,
  ]
    .filter(Boolean)
    .join(" ");

  if (!isCompact && config.id) {
    notification.dataset.feedbackId = config.id;
  }

  if (!isCompact && config.title !== undefined) {
    const title = document.createElement("div");
    title.className = "feedback-ui-notification-title";
    appendText(title, config.title);
    notification.appendChild(title);
  }

  const content = document.createElement("div");
  content.className = "feedback-ui-notification-content";
  appendText(content, config.content);
  notification.appendChild(content);

  if (!isCompact && config.actionText !== undefined) {
    const action = document.createElement("button");
    action.type = "button";
    action.className = "feedback-ui-notification-action";
    appendText(action, config.actionText);
    action.addEventListener("click", (event) => {
      event.stopPropagation();
      config.onAction?.();
    });
    notification.appendChild(action);
  }

  container.appendChild(notification);
  scheduleRemove(
    notification,
    config.duration,
    isCompact ? DEFAULT_COMPACT_DURATION : DEFAULT_NOTIFICATION_DURATION
  );

  return notification;
};

const createNotification = (type: FeedbackType, input: FeedbackInput) => {
  if (suspendedNotifications.size > 0) {
    queuedNotifications.push({ type, input });
    return undefined;
  }

  return createFeedback(type, input, "standard");
};

const removeNotification = (id: string) => {
  queuedNotifications = queuedNotifications.filter((queued) => {
    const config = normalizeConfig(queued.input);
    return config.id !== id;
  });

  if (!canUseDom()) {
    return;
  }

  document
    .querySelectorAll(".feedback-ui-notification-standard[data-feedback-id]")
    .forEach((node) => {
      if ((node as HTMLElement).dataset.feedbackId === id) {
        node.remove();
      }
    });
};

const suspendNotification = (key: string) => {
  suspendedNotifications.add(key);
};

const resumeNotification = (key: string) => {
  suspendedNotifications.delete(key);

  if (suspendedNotifications.size > 0 || queuedNotifications.length === 0) {
    return;
  }

  const notifications = queuedNotifications;
  queuedNotifications = [];
  notifications.forEach(({ type, input }) => {
    createNotification(type, input);
  });
};

export const feedback = {
  success: (input: FeedbackInput) => createFeedback("success", input, "compact"),
  error: (input: FeedbackInput) => createFeedback("error", input, "compact"),
  warning: (input: FeedbackInput) => createFeedback("warning", input, "compact"),
  info: (input: FeedbackInput) => createFeedback("info", input, "compact"),
  notification: {
    success: (input: FeedbackInput) => createNotification("success", input),
    error: (input: FeedbackInput) => createNotification("error", input),
    warning: (input: FeedbackInput) => createNotification("warning", input),
    info: (input: FeedbackInput) => createNotification("info", input),
    remove: (id: string) => removeNotification(id),
    suspend: (key: string) => suspendNotification(key),
    resume: (key: string) => resumeNotification(key),
  },
};

export default feedback;
